import * as Yup from 'yup';
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useForm } from 'react-hook-form';

// form
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import {
  Stack,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  AlertTitle,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Iconify from '../../../components/Iconify';
import 'react-dropzone-uploader/dist/styles.css';
// components
import Label from '../../../components/Label';
import { FormProvider, RHFTextField } from '../../../components/hook-form';
import tracker from '../../../api/tracker';
import ImageUploaderWithPreview from '../../../components/hook-form/ImageUploaderWithPreview';

// ----------------------------------------------------------------------

ResourceForm.propTypes = {
  resourceData: PropTypes.object,
};

ResourceForm.defaultValues = {
  resourceData: Object(),
};

export default function ResourceForm({ resourceData }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isAlertVisible, setAlertVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);

  const defaultValues = {
    name: '',
    url: '',
    icon_photo: '',
  };
  const resourceSchema = Yup.object().shape({
    name: Yup.string().required(t('Required')),
    url: Yup.string().required(t('Required')).url(t('invalid_url') || 'Please enter a valid URL'),
  });
  const methods = useForm({
    resolver: yupResolver(resourceSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    setValue,
    setError,
  } = methods;

  useEffect(() => {
    if (resourceData) {
      const fields = ['name', 'hashid', 'url'];
      fields.forEach((field) => setValue(field, resourceData[field]));
    }
  }, [resourceData, setValue]);

  const onSubmit = (data) => {
    console.log(data.icon_photo[0]);
    if (data.icon_photo[0]) {
      data.icon_photo = data.icon_photo[0];
    }
    setIsLoading(true);
    console.log(resourceData);
    const formSubmission = resourceData
      ? tracker.put(`resources/${resourceData.hashid}`, data)
      : tracker.post('resources', data, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

    formSubmission
      .then(() => {
        setAlertVisible(false);
        navigate('/dashboard/resources');
      })
      .catch((err) => {
        setIsLoading(false);
        setHasError(true);
        setAlertVisible(true);

        // Parse backend validation errors (422) and show field-level messages
        const responseErrors = err?.response?.data?.errors;
        if (responseErrors && typeof responseErrors === 'object') {
          const messages = [];
          Object.entries(responseErrors).forEach(([field, msgs]) => {
            const fieldMessages = Array.isArray(msgs) ? msgs : [msgs];
            // Map known fields to form fields
            if (['name', 'url', 'icon_photo'].includes(field)) {
              setError(field, { type: 'server', message: fieldMessages[0] });
            }
            messages.push(...fieldMessages);
          });
          setErrorMessages(messages);
        } else {
          setErrorMessages([]);
        }

        console.error(err);
      });
  };

  const onDelete = async () => {
    try {
      const deleteResponse = await tracker.delete(`resources/${resourceData.hashid}`);

      if (deleteResponse.status === 'OK') {
        setHasError(false);
        navigate('/dashboard/resources');
      } else {
        throw Error('Response was not ok');
      }
    } catch (err) {
      setHasError(true);
      setAlertVisible(true);
      setIsDeleting(false);
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Collapse in={isAlertVisible}>
        <Alert
          severity={hasError ? 'error' : 'success'}
          sx={{ mb: 5 }}
          onClose={() => {
            setAlertVisible(false);
          }}
        >
          <AlertTitle>{hasError ? t('Something_went_wrong') : t('Success')}</AlertTitle>
          {hasError && errorMessages.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {errorMessages.map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          )}
        </Alert>
      </Collapse>
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', sm: 'column' }} spacing={2}>
          <RHFTextField name="name" label={t('Resource_name')} disabled={isLoading} />
          <RHFTextField name="url" label={t('UResource_RL')} disabled={isLoading} />
          <div style={{ marginTop: '80px' }}>
            <Label>{t('image')}</Label>
            <ImageUploaderWithPreview name="icon_photo" currentUrl={resourceData?.icon_photo || ''} />
          </div>
          <input type="hidden" name="hashid" />
        </Stack>
        <LoadingButton fullWidth size="large" type="submit" variant="contained" loading={isLoading || isDeleting}>
          {t('Save')}
        </LoadingButton>
        {!!resourceData && (
          <Stack direction="row" alignItems="center" justifyContent="space-around" mt={5}>
            <Button
              variant="contained"
              color="error"
              startIcon={<Iconify icon="quill:folder-trash" />}
              onClick={() => {
                setIsDeleting(true);
              }}
              disabled={isLoading || isDeleting}
            >
              {t('Remove')}
            </Button>
            <Dialog open={isDeleting}>
              <DialogTitle>{t('Confirmation')}</DialogTitle>
              <DialogContent>
                <DialogContentText>{t('Resource_delete_confirmation')}</DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => {
                    setIsDeleting(false);
                  }}
                >
                  Cancel
                </Button>
                <Button onClick={onDelete}>{t('Remove')}</Button>
              </DialogActions>
            </Dialog>
          </Stack>
        )}
      </Stack>
    </FormProvider>
  );
}
